import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, X, User, ChevronDown, ChevronRight, UserCheck } from 'lucide-react';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { WOStatusBadge, WOTypeBadge, ImpactBadge } from '@/components/ui/status-badges';
import { CreateWorkOrderDialog } from '@/components/work-orders/CreateWorkOrderDialog';
import { useWorkOrders } from '@/stores/workOrderStore';
import { useAuth } from '@/contexts/AuthContext';
import { usePermission } from '@/hooks/usePermission';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchProfiles } from '@/lib/api/profiles';
import { fetchWorkOrder } from '@/lib/api/workOrders';
import type { WorkOrder } from '@/types/maintenance';
import { differenceInHours, differenceInDays, isToday } from 'date-fns';
import { cn } from '@/lib/utils';
import { usePagination } from '@/hooks/usePagination';
import { PaginationControls } from '@/components/ui/pagination-controls';
import { toast } from 'sonner';

// ============ FILTER DEFINITIONS ============

interface FilterChip {
  key: string;
  labelKey: string;   // i18n key under maintenance:wo.filter.*
  group: 'status' | 'type' | 'assignment' | 'time' | 'impact';
  match: (wo: WorkOrder) => boolean;
}

// Static chips — 'mine' match is overridden in chipFiltered using current user.
// Labels resolved via t() at render time.
const FILTER_CHIPS: FilterChip[] = [
  { key: 'mine',         labelKey: 'maintenance:wo.filter.my_work',        group: 'assignment', match: () => true },
  { key: 'unassigned',   labelKey: 'maintenance:wo.filter.unassigned',      group: 'assignment', match: wo => !wo.assignee },
  { key: 'line_down',    labelKey: 'maintenance:wo.filter.line_down',       group: 'impact',     match: wo => wo.impact === 'line_down' },
  { key: 'safety',       labelKey: 'maintenance:wo.filter.safety',          group: 'impact',     match: wo => wo.impact === 'safety' },
  { key: 'in_progress',  labelKey: 'maintenance:wo.filter.in_maintenance',  group: 'status',     match: wo => wo.status === 'in_progress' },
  { key: 'requested',    labelKey: 'maintenance:wo.filter.ready_to_start',  group: 'status',     match: wo => wo.status === 'requested' },
  { key: 'closed_today', labelKey: 'maintenance:wo.filter.closed_today',    group: 'time',       match: wo => wo.status === 'closed' && !!wo.end_time && isToday(new Date(wo.end_time)) },
];

function parseFiltersFromParams(params: URLSearchParams): Set<string> {
  const filters = new Set<string>();
  const filtersParam = params.get('filters');
  if (filtersParam) {
    filtersParam.split(',').forEach(f => {
      if (FILTER_CHIPS.some(c => c.key === f)) filters.add(f);
    });
  }
  return filters;
}

function filtersToParams(filters: Set<string>, tab: string): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.size > 0) {
    params.set('filters', Array.from(filters).join(','));
  }
  if (tab !== 'open') {
    params.set('tab', tab);
  }
  return params;
}

function deriveTab(filters: Set<string>, fallback: string): 'open' | 'blocked' | 'closed' | 'team' {
  if (fallback === 'blocked' || fallback === 'closed' || fallback === 'team') return fallback as 'blocked' | 'closed' | 'team';
  return 'open';
}

export default function WorkOrders() {
  const { t } = useTranslation(['maintenance', 'common']);
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [createWOOpen, setCreateWOOpen] = useState(false);
  const queryClient = useQueryClient();

  const prefetchWO = useCallback((id: string) => {
    queryClient.prefetchQuery({
      queryKey: ['work_order', id],
      queryFn: () => fetchWorkOrder(id),
      staleTime: 60_000,
    });
  }, [queryClient]);

  // Parse filters from URL
  const activeFilters = useMemo(() => parseFiltersFromParams(searchParams), [searchParams]);
  const tabParam = searchParams.get('tab') || 'open';
  const activeTab = deriveTab(activeFilters, tabParam);

  const { workOrders, loading, reassignWorkOrder } = useWorkOrders();
  const { user } = useAuth();
  const { can } = usePermission();
  const isManager = can('work_orders:manage');

  // Selection state for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback((wos: WorkOrder[]) => {
    setSelectedIds(prev => {
      const allSelected = wos.length > 0 && wos.every(w => prev.has(w.id));
      return allSelected ? new Set() : new Set(wos.map(w => w.id));
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  // Clear selection when tab changes
  useEffect(() => { clearSelection(); }, [activeTab, clearSelection]);

  const handleBulkAssign = useCallback(async (userId: string) => {
    const ids = Array.from(selectedIds);
    try {
      await Promise.all(ids.map(id => reassignWorkOrder(id, userId)));
      toast.success(`Assigned ${ids.length} work order${ids.length !== 1 ? 's' : ''}`);
      clearSelection();
    } catch {
      toast.error(t('maintenance:wo.toast.assign_failed'));
    }
  }, [selectedIds, reassignWorkOrder, clearSelection]);

  // Profiles for bulk assign dropdown (managers only)
  const { data: profiles = [] } = useQuery({
    queryKey: ['profiles', user?.organization_id],
    queryFn: () => fetchProfiles(user?.organization_id ?? null),
    enabled: !!user && isManager,
    staleTime: 10 * 60 * 1000,
  });

  // Technicians default to "My Work" on first load (no filters in URL)
  useEffect(() => {
    if (user?.role === 'technician' && !searchParams.get('filters') && !searchParams.get('tab')) {
      setSearchParams({ filters: 'mine' }, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // ============ FILTER TOGGLE ============

  const toggleFilter = useCallback((key: string) => {
    const next = new Set(activeFilters);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    setSearchParams(filtersToParams(next, activeTab));
  }, [activeFilters, activeTab, setSearchParams]);

  const clearAllFilters = useCallback(() => {
    setSearchParams(activeTab !== 'open' ? { tab: activeTab } : {});
  }, [activeTab, setSearchParams]);

  const handleTabChange = useCallback((tab: string) => {
    // Keep filters when switching tabs
    const params = filtersToParams(activeFilters, tab);
    setSearchParams(params);
  }, [activeFilters, setSearchParams]);

  // ============ FILTERING LOGIC ============

  const now = useMemo(() => new Date(), []);

  const chipDefs = useMemo(
    () => FILTER_CHIPS.filter(c => activeFilters.has(c.key)),
    [activeFilters],
  );

  const searchFiltered = useMemo(() => {
    if (!searchTerm) return workOrders;
    const term = searchTerm.toLowerCase();
    return workOrders.filter(
      (wo) =>
        wo.wo_id.toLowerCase().includes(term) ||
        wo.mold?.name?.toLowerCase().includes(term) ||
        wo.mold?.mold_id?.toLowerCase().includes(term) ||
        wo.problem_description.toLowerCase().includes(term)
    );
  }, [workOrders, searchTerm]);

  const chipFiltered = useMemo(() => {
    if (chipDefs.length === 0) return searchFiltered;
    return searchFiltered.filter(wo => chipDefs.every(chip => {
      if (chip.key === 'mine') return wo.assigned_to === user?.id || wo.assignee?.id === user?.id;
      return chip.match(wo);
    }));
  }, [searchFiltered, chipDefs, user?.id]);

  const openWOs   = useMemo(() => chipFiltered.filter(wo => wo.status !== 'closed' && wo.status !== 'blocked'), [chipFiltered]);
  const blockedWOs = useMemo(() => chipFiltered.filter(wo => wo.status === 'blocked'), [chipFiltered]);
  const closedWOs  = useMemo(() => chipFiltered.filter(wo => wo.status === 'closed'),  [chipFiltered]);

  const currentList = activeTab === 'open' ? openWOs : activeTab === 'blocked' ? blockedWOs : closedWOs;

  const getRowHighlight = useCallback((wo: WorkOrder) => {
    if (wo.status === 'blocked' && wo.blocked_at && differenceInHours(now, new Date(wo.blocked_at)) >= 24) return 'bg-[hsl(var(--status-red-bg))]/20';
    if (wo.status !== 'closed' && differenceInDays(now, new Date(wo.created_at)) >= 3) return 'bg-[hsl(var(--status-amber-bg))]/20';
    return '';
  }, [now]);

  const getFailureDisplay = (wo: WorkOrder) => {
    return wo.failure_text || wo.problem_description;
  };

  // Show assignment + impact chips as toggle buttons; status/time chips come from dashboard links
  const visibleChips = FILTER_CHIPS.filter(c => c.group === 'assignment' || c.group === 'impact');

  // Team workload (manager view) — group all open WOs by assignee
  const workloadGroups = useMemo(() => {
    const allOpen = workOrders.filter(wo => wo.status !== 'closed');
    const groups: Map<string, { name: string; wos: WorkOrder[] }> = new Map();
    for (const wo of allOpen) {
      const key = wo.assignee?.id ?? '__unassigned__';
      const name = wo.assignee?.name ?? 'Unassigned';
      if (!groups.has(key)) groups.set(key, { name, wos: [] });
      groups.get(key)!.wos.push(wo);
    }
    // Sort: assigned techs alphabetically first, Unassigned last
    return Array.from(groups.entries()).sort(([ka], [kb]) => {
      if (ka === '__unassigned__') return 1;
      if (kb === '__unassigned__') return -1;
      return groups.get(ka)!.name.localeCompare(groups.get(kb)!.name);
    }).map(([key, g]) => ({ key, ...g }));
  }, [workOrders]);

  return (
    <div className="min-h-screen bg-background">
      <PageHeader
        title={t('maintenance:wo.list_title')}
        showSearch
        searchPlaceholder={t('maintenance:wo.search_placeholder', { defaultValue: 'Search by WO#, mold, or description...' })}
        onSearch={setSearchTerm}
        actions={
          <Button size="sm" onClick={() => setCreateWOOpen(true)} className="h-9">
            <Plus className="h-4 w-4 mr-1.5" />
            {t('maintenance:wo.create_title')}
          </Button>
        }
      />

      <div className="p-6 max-w-[1400px] mx-auto">
        {/* ============ TABS ============ */}
        <div className="inline-flex items-center rounded-lg bg-muted p-1 mb-3">
          {([
            { value: 'open' as const, label: t('maintenance:wo.tab_open'), count: openWOs.length },
            { value: 'blocked' as const, label: t('maintenance:wo.tab_blocked'), count: blockedWOs.length },
            { value: 'closed' as const, label: t('maintenance:wo.tab_closed'), count: closedWOs.length },
            ...(isManager ? [{ value: 'team' as const, label: t('maintenance:wo.tab_by_tech'), count: workloadGroups.length }] : []),
          ]).map(tab => (
            <button
              key={tab.value}
              onClick={() => handleTabChange(tab.value)}
              className={cn(
                'px-4 py-1.5 rounded-md text-sm font-medium transition-all',
                activeTab === tab.value
                  ? 'bg-card text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {tab.label}
              <span className="ml-1.5 text-muted-foreground/60 text-[11px]">{tab.count}</span>
            </button>
          ))}
        </div>

        {/* ============ FILTER BAR ============ */}
        <div className="flex items-center gap-2 flex-wrap mb-3">
          {visibleChips.map(chip => {
            const isActive = activeFilters.has(chip.key);
            const impactActive = chip.key === 'line_down'
              ? 'bg-[hsl(var(--status-red-bg))] text-[hsl(var(--status-red))] border-[hsl(var(--status-red)/0.4)]'
              : chip.key === 'safety'
              ? 'bg-[hsl(var(--priority-high)/0.1)] text-[hsl(var(--priority-high))] border-[hsl(var(--priority-high)/0.4)]'
              : 'bg-molten/10 text-molten-deep border-molten/20';
            return (
              <button
                key={chip.key}
                onClick={() => toggleFilter(chip.key)}
                className={cn(
                  'inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition-all cursor-pointer',
                  'border',
                  isActive
                    ? impactActive
                    : 'bg-muted/50 text-muted-foreground border-transparent hover:bg-muted hover:text-foreground'
                )}
              >
                {t(chip.labelKey)}
                {isActive && <X className="h-3 w-3 ml-0.5 opacity-60" />}
              </button>
            );
          })}
          {activeFilters.size > 0 && (
            <button
              onClick={clearAllFilters}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors ml-1"
            >
              {t('common:action.clear')}
            </button>
          )}
        </div>

        {/* ============ ACTIVE FILTER INDICATOR ============ */}
        {activeFilters.size > 0 && (
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs text-muted-foreground">{t('common:action.filter')}:</span>
            {Array.from(activeFilters).map(key => {
              const chip = FILTER_CHIPS.find(c => c.key === key);
              return (
                <span
                  key={key}
                  className="inline-flex items-center gap-1 rounded-md bg-molten/10 text-molten-deep px-2 py-0.5 text-xs font-medium"
                >
                  {chip ? t(chip.labelKey) : key}
                  <button onClick={() => toggleFilter(key)} className="hover:text-molten-deep/70 transition-colors">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              );
            })}
          </div>
        )}

        {/* ============ TABLE or TEAM VIEW ============ */}
        {activeTab === 'team' ? (
          <TeamWorkloadView groups={workloadGroups} />
        ) : (
          <div className="bg-card rounded-lg border border-border/50">
            {loading ? (
              <div className="p-4 space-y-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="h-4 w-28 bg-muted animate-pulse rounded" />
                    <div className="h-4 w-20 bg-muted animate-pulse rounded" />
                    <div className="h-4 w-24 bg-muted animate-pulse rounded" />
                    <div className="h-4 w-20 bg-muted animate-pulse rounded" />
                    <div className="h-4 w-24 bg-muted animate-pulse rounded" />
                    <div className="h-4 flex-1 bg-muted animate-pulse rounded" />
                  </div>
                ))}
              </div>
            ) : (
              <WorkOrderTable
                workOrders={currentList}
                getRowHighlight={getRowHighlight}
                getFailureDisplay={getFailureDisplay}
                selectedIds={selectedIds}
                onToggleSelect={isManager ? handleToggleSelect : undefined}
                onSelectAll={isManager ? handleSelectAll : undefined}
              />
            )}
          </div>
        )}
      </div>

      <CreateWorkOrderDialog open={createWOOpen} onOpenChange={setCreateWOOpen} />

      {/* ============ FLOATING BULK ACTION TOOLBAR ============ */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-card border border-border shadow-xl rounded-xl px-4 py-2.5 animate-in fade-in slide-in-from-bottom-2 duration-200">
          <span className="text-sm font-semibold">{selectedIds.size} selected</span>
          <div className="w-px h-4 bg-border" />
          <div className="flex items-center gap-1.5">
            <UserCheck className="h-3.5 w-3.5 text-muted-foreground" />
            <Select onValueChange={handleBulkAssign}>
              <SelectTrigger className="h-7 text-xs w-40 border-border/60">
                <SelectValue placeholder={t('maintenance:wo.assign_to_placeholder')} />
              </SelectTrigger>
              <SelectContent>
                {profiles.filter(p => p.role !== 'super_admin').map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-px h-4 bg-border" />
          <button
            onClick={clearSelection}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
          >
            <X className="h-3.5 w-3.5" />
            Clear
          </button>
        </div>
      )}
    </div>
  );
}

// ============ TABLE COMPONENT ============

function WorkOrderTable({
  workOrders: wos,
  getRowHighlight,
  getFailureDisplay,
  selectedIds,
  onToggleSelect,
  onSelectAll,
}: {
  workOrders: WorkOrder[];
  getRowHighlight: (wo: WorkOrder) => string;
  getFailureDisplay: (wo: WorkOrder) => string;
  selectedIds?: Set<string>;
  onToggleSelect?: (id: string) => void;
  onSelectAll?: (wos: WorkOrder[]) => void;
}) {
  const { t } = useTranslation(['maintenance']);
  const pagination = usePagination(wos, 25);
  const hasSelection = !!onToggleSelect && !!selectedIds;
  const allSelected = hasSelection && pagination.items.length > 0 && pagination.items.every(w => selectedIds!.has(w.id));
  const someSelected = hasSelection && pagination.items.some(w => selectedIds!.has(w.id));
  const colSpan = hasSelection ? 8 : 7;

  return (
    <div className="overflow-x-auto">
      <table className="data-table">
        <thead>
          <tr>
            {hasSelection && (
              <th className="w-8">
                <Checkbox
                  checked={allSelected}
                  data-state={someSelected && !allSelected ? 'indeterminate' : undefined}
                  onCheckedChange={() => onSelectAll!(pagination.items)}
                  aria-label={t('maintenance:wo.select_all')}
                  className="h-3.5 w-3.5"
                />
              </th>
            )}
            <th>{t('maintenance:wo.id')}</th>
            <th>{t('maintenance:wo.impact')}</th>
            <th>{t('maintenance:wo.type')}</th>
            <th>{t('maintenance:wo.mold')}</th>
            <th>{t('maintenance:wo.status')}</th>
            <th>{t('maintenance:wo.assigned_to')}</th>
            <th>{t('maintenance:wo.failure')}</th>
          </tr>
        </thead>
        <tbody>
          {wos.length === 0 ? (
            <tr>
              <td colSpan={colSpan} className="text-center text-muted-foreground py-8">
                {t('maintenance:wo.no_work_orders')}
              </td>
            </tr>
          ) : (
            pagination.items.map((wo) => {
              const isSelected = hasSelection && selectedIds!.has(wo.id);
              return (
                <tr
                  key={wo.id}
                  className={cn(getRowHighlight(wo), isSelected && 'bg-molten/5 hover:bg-molten/10')}
                  onMouseEnter={() => prefetchWO(wo.id)}
                >
                  {hasSelection && (
                    <td className="w-8" onClick={e => e.stopPropagation()}>
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => onToggleSelect!(wo.id)}
                        aria-label={`Select ${wo.wo_id}`}
                        className="h-3.5 w-3.5"
                      />
                    </td>
                  )}
                  <td className="whitespace-nowrap">
                    <Link to={`/maintenance/work-orders/${wo.id}`} className="font-semibold text-primary hover:underline">
                      {wo.wo_id}
                    </Link>
                  </td>
                  <td><ImpactBadge impact={wo.impact} size="sm" /></td>
                  <td><WOTypeBadge type={wo.type} size="sm" /></td>
                  <td>
                    <div>
                      <span className="font-medium text-sm">{wo.mold?.mold_id}</span>
                      <span className="text-xs text-muted-foreground/60 ml-1.5">{wo.mold?.name}</span>
                    </div>
                  </td>
                  <td><WOStatusBadge status={wo.status} blockedReason={wo.blocked_reason} size="sm" /></td>
                  <td>
                    {wo.assignee ? (
                      <div>
                        <span className="text-sm">{wo.assignee.name}</span>
                        {wo.status !== 'closed' && wo.eta_to_restore && (
                          <p className={cn(
                            'text-[11px] mt-0.5',
                            wo.eta_confidence === 'high' ? 'text-[hsl(var(--status-green))]' :
                            wo.eta_confidence === 'medium' ? 'text-[hsl(var(--status-amber))]' :
                            'text-[hsl(var(--status-red))]',
                          )}>
                            ETA {new Date(wo.eta_to_restore).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' })}
                          </p>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted-foreground/50 text-xs">{t('maintenance:wo.unassigned')}</span>
                    )}
                  </td>
                  <td className="max-w-[200px]">
                    <p className="text-sm text-muted-foreground line-clamp-1">{getFailureDisplay(wo)}</p>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      {wos.length > 0 && (
        <PaginationControls
          page={pagination.page}
          totalPages={pagination.totalPages}
          total={pagination.total}
          pageSize={pagination.pageSize}
          canPrev={pagination.canPrev}
          canNext={pagination.canNext}
          onPrev={() => pagination.setPage(pagination.page - 1)}
          onNext={() => pagination.setPage(pagination.page + 1)}
          label="work orders"
        />
      )}
    </div>
  );
}

// ============ TEAM WORKLOAD VIEW ============

function TeamWorkloadView({ groups }: { groups: { key: string; name: string; wos: WorkOrder[] }[] }) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (key: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  if (groups.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border/50 p-8 text-center text-muted-foreground text-sm">
        No open work orders.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {groups.map(g => {
        const isOpen = expanded.has(g.key);
        const blockedCount = g.wos.filter(wo => wo.status === 'blocked').length;
        return (
          <div key={g.key} className="bg-card rounded-lg border border-border/50 overflow-hidden">
            <button
              onClick={() => toggle(g.key)}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/30 transition-colors"
            >
              {isOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
              <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <span className="text-xs font-semibold text-primary">{g.name.charAt(0).toUpperCase()}</span>
              </div>
              <span className="font-medium text-sm flex-1">{g.name}</span>
              <span className="text-xs text-muted-foreground">{g.wos.length} open</span>
              {blockedCount > 0 && (
                <span className="text-xs text-[hsl(var(--status-red))] ml-2">{blockedCount} blocked</span>
              )}
            </button>
            {isOpen && (
              <div className="border-t border-border/50">
                {g.wos.map(wo => (
                  <Link
                    key={wo.id}
                    to={`/maintenance/work-orders/${wo.id}`}
                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-muted/20 transition-colors border-b border-border/30 last:border-b-0"
                    onMouseEnter={() => prefetchWO(wo.id)}
                  >
                    <span className="font-mono text-xs text-primary font-semibold w-28 shrink-0">{wo.wo_id}</span>
                    <WOTypeBadge type={wo.type} size="sm" />
                    <span className="text-sm flex-1 truncate text-muted-foreground">{wo.mold?.mold_id} — {wo.problem_description}</span>
                    <WOStatusBadge status={wo.status} blockedReason={wo.blocked_reason} size="sm" />
                  </Link>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
